import { cn } from '@neo/lib/utils'
import type { ButtonProps } from '@neo/primitives/button'
import { StartKycButton, useStartKyc } from '../phone-numbers/start-kyc'
import { useIndiaKycDialogStore } from './india-kyc-dialog-store'
import { INDIA_KYC_PERMISSION, useCanUseIndiaKyc } from './queries'
import { useBusinessKycAvailability } from './use-india-individual-kyc'

/**
 * What the upgrade button and `?action=upgrade` open: the upgrade, the restart of a stopped
 * business KYC, or the one already under way.
 */
export const useBusinessKycUpgradeLink = (enabled = true) => {
  const openUpgrade = useIndiaKycDialogStore((state) => state.openUpgrade)
  const openProgress = useIndiaKycDialogStore((state) => state.openProgress)
  const restart = useRestartBusinessKyc()
  const { isRead, canStart, isOpen, isStopped } = useBusinessKycAvailability(enabled)
  const open = () => {
    if (canStart && isStopped) restart()
    else if (canStart) openUpgrade()
    else if (isOpen) openProgress()
  }
  return { isRead, canStart, isOpen, isStopped, open }
}

/** Restart KYC on a stopped business KYC: the signatory form, then the new attempt's timeline. */
export const useRestartBusinessKyc = () => {
  const openBusiness = useIndiaKycDialogStore((state) => state.openBusiness)
  return useStartKyc(() => openBusiness('progress'))
}

interface UpgradeToBusinessKycProps {
  /** Test ids `<prefix>-upgrade-btn`, or `<prefix>-restart-btn` once the last one stopped. */
  testIdPrefix: string
  /** `card` adds what business KYC is for, beside the button. */
  variant?: 'button' | 'card'
  size?: ButtonProps['size']
  buttonVariant?: ButtonProps['variant']
  /** On the card, or on the button alone. */
  className?: string
}

// The one business KYC call to action. It opens in place: the app shell hosts the dialogs.
export const UpgradeToBusinessKyc = ({
  testIdPrefix,
  variant = 'button',
  size,
  buttonVariant,
  className,
}: UpgradeToBusinessKycProps) => {
  const { canStart, isOpen, isStopped, open } = useBusinessKycUpgradeLink()
  // Checked here too, so the card never stands without its button.
  const canUseKyc = useCanUseIndiaKyc()
  if (!canUseKyc || (!canStart && !isOpen)) return null
  const isCard = variant === 'card'
  const button = (
    <StartKycButton
      className={isCard ? 'shrink-0' : className}
      size={size}
      variant={buttonVariant}
      label={isStopped ? 'Restart business KYC' : 'Upgrade to business KYC'}
      permission={INDIA_KYC_PERMISSION}
      onClick={open}
      testId={`${testIdPrefix}-${isStopped ? 'restart' : 'upgrade'}-btn`}
    />
  )
  if (!isCard) return button
  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
      data-testid={`${testIdPrefix}-business-kyc-card`}
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-p-600">Business KYC</h3>
        <p className="text-s-400 text-muted-foreground">
          Unlocks extended limits for your account.
        </p>
      </div>
      {button}
    </div>
  )
}
